import * as Sentry from "@sentry/browser";
import { Spin, message } from "antd";
import React, { Fragment, RefObject } from "react";
import BaseForm from "../../components/Form/BaseForm";
import StepContainer from "../../components/StepContainer";
import {
  APIError,
  Company,
  ContributionTypesEnum,
  Country,
  FormFieldConfig,
  Opportunity,
  PaymentTypesEnum,
  Product,
  SalesPerson,
  SelectOptionsInterface,
} from "../../helpers/shared-interfaces";

import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import ModalCreator from "../../components/ModalCreator";
import { GET_SALES_PEOPLE } from "../../gql/users";
import {
  convertCompaniesToSelectOptions,
  generateContributionTypes,
  generateDisplayErrorMessage,
  getUniqueArrayObjects,
  handleValidationErrors,
  mapAPIErrorToFormError,
  preTaxProducts,
  productPaymentTypes,
  request,
  responseHandler,
  isSavingsBoosterProduct,
} from "../../helpers/utilities";
import {
  ASSIGN_EMPLOYER_TO_OPPORTUNITY,
  ASSIGN_PRODUCT_TO_OPPORTUNITY,
} from "../../gql/opportunity";
import { SEARCH_COMPANY_SUMMARY } from "../../gql/companies";
import { GET_COUNTRIES } from "../../gql/general";
import { contributionTypes, DEBOUNCE_TIME } from "../../helpers/constants";

interface AssignProductToOpportunityProps {
  product: string;
  monthlyExpectedContribution?: string;
  lumpsumExpectedContribution?: string;
  sourceOfAwareness?: string;
  signupReason?: string;
  productPaymentType?: string;
  contributionType?: string;
  contributionAllocationTypes?: string[];
  salesPersonId?: string;
}
interface AssignEmployerToOpportunityProps {
  employerId?: string;
  employerName?: string;
  employerEmailAddress?: string;
}

interface Awareness {
  id: string;
  name: string;
  value: string;
}

const sourceOfAwareness: Awareness[] = [
  { id: "1", name: "Social Media", value: "Social Media" },
  { id: "2", name: "Family", value: "Family" },
  { id: "3", name: "Billboards", value: "Billboards" },
  { id: "4", name: "Radio", value: "Radio" },
  { id: "5", name: "Newspapers/Magazines", value: "Newspapers/Magazines" },
  {
    id: "6",
    name: "Word of mouth/Referrals",
    value: "Word of mouth/Referrals",
  },

  {
    id: "7",
    name: "Petra Representative",
    value: "Petra Representative",
  },

  {
    id: "8",
    name: "Email From Petra Trust",
    value: "Email From Petra Trust",
  },

  {
    id: "9",
    name: "Website",
    value: "Website",
  },
];

const signUpReason: SelectOptionsInterface[] = [
  { id: "1", name: "I trust Petra", value: "I trust Petra" },
  {
    id: "2",
    name: "I am diversifying my investment",
    value: "I am diversifying my investment",
  },
  {
    id: "3",
    name: "I am trying the product",
    value: "I am trying the product",
  },
  {
    id: "4",
    name: "The return attracted me",
    value: "The return attracted me",
  },
  { id: "5", name: "A friend recommended", value: "A friend recommended" },
  { id: "6", name: "The product's features", value: "The product's features" },
  {
    id: "7",
    name: "Required by law for institutions to pay for employees",
    value: "Required by law for institutions to pay for employees",
  },
];

interface ProductFormState {
  isLoading: boolean;
  fieldsToHide: string[];
  productCategoryAndTier: string;
  pretaxProductName: string | undefined;
  companies: Company[];
  q: string;
  showEmployerModal: boolean;
  selectingEmployer: boolean;
  newCompany: boolean;
  selectedContributionType: boolean;
  selectedMonthlyAndLumpSumContributionType: boolean;
  countries: Country[];
  salesPersonSearchTerm: string;
  salesPersons: SalesPerson[];
}

interface ProductFormProps {
  petraProducts: Product[];
  opportunity: Opportunity;
  opportunityPreview: JSX.Element | null;
  next: () => void;
  prev: () => void;
  setPercentage: (percentage: number) => void;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;
  editingState?: boolean;
  save?: () => void;
  cancelEdit?: () => void;
}

export function remodelProducts(products: Product[]): SelectOptionsInterface[] {
  const remodeledArray: SelectOptionsInterface[] = [];
  for (const product of products) {
    remodeledArray.push({
      id: product.id,
      name: product.name,
    });
  }
  return remodeledArray;
}

const savingsBoosterContributionAllocationTypes = [
  "employee_pre_tax",
  "employee_post_tax",
];

export function getContributionAllocationTypeOptions(
  productName?: string,
): SelectOptionsInterface[] {
  if (!isSavingsBoosterProduct(productName)) {
    return contributionTypes;
  }

  return contributionTypes.filter((type) =>
    savingsBoosterContributionAllocationTypes.includes(type.id),
  );
}

export function sanitizeContributionAllocationTypes(
  values: string[] = [],
  productName?: string,
): string[] {
  const allowedTypes = new Set(
    getContributionAllocationTypeOptions(productName).map(({ id }) => id),
  );

  return values.filter((value) => allowedTypes.has(value));
}

export function getContributionAmountFieldState(contributionType?: string): {
  hiddenFields: string[];
  monthlyDisabled: boolean;
  lumpSumDisabled: boolean;
} {
  if (contributionType === ContributionTypesEnum.lumpSum) {
    return {
      hiddenFields: ["monthlyExpectedContribution"],
      monthlyDisabled: true,
      lumpSumDisabled: false,
    };
  }

  if (contributionType === ContributionTypesEnum.monthlyContribution) {
    return {
      hiddenFields: ["lumpsumExpectedContribution"],
      monthlyDisabled: false,
      lumpSumDisabled: true,
    };
  }

  if (contributionType === ContributionTypesEnum.monthlyAndLump) {
    return {
      hiddenFields: [],
      monthlyDisabled: false,
      lumpSumDisabled: false,
    };
  }

  return {
    hiddenFields: ["monthlyExpectedContribution", "lumpsumExpectedContribution"],
    monthlyDisabled: true,
    lumpSumDisabled: true,
  };
}

export function normalizeContributionAmountPayload<T extends {
  contributionType?: string;
  monthlyExpectedContribution?: unknown;
  lumpsumExpectedContribution?: unknown;
}>(payload: T): T {
  const state = getContributionAmountFieldState(payload.contributionType);

  return {
    ...payload,
    monthlyExpectedContribution: state.monthlyDisabled
      ? null
      : payload.monthlyExpectedContribution,
    lumpsumExpectedContribution: state.lumpSumDisabled
      ? null
      : payload.lumpsumExpectedContribution,
  };
}

export default class ProductForm extends React.Component<
  ProductFormProps,
  ProductFormState
> {
  reference: RefObject<FormInstance>;
  employerReference: RefObject<FormInstance>;

  constructor(props: ProductFormProps) {
    super(props);
    this.state = {
      isLoading: true,
      fieldsToHide: [
        "monthlyExpectedContribution",
        "lumpsumExpectedContribution",
        "employerId",
        "employerName",
        "employerEmailAddress",
      ],
      productCategoryAndTier: "",
      pretaxProductName: "",
      companies: [],
      q: "",
      showEmployerModal: false,
      selectingEmployer: false,
      selectedContributionType: false,
      selectedMonthlyAndLumpSumContributionType: false,
      newCompany: false,
      countries: [],
      salesPersonSearchTerm: "",
      salesPersons: [],
    };
    this.reference = React.createRef<FormInstance>();
    this.employerReference = React.createRef<FormInstance>();
    this.next = this.next.bind(this);
  }

  generateProductPaymentTypes = (): SelectOptionsInterface[] => {
    const { opportunity } = this.props;
    const { pretaxProductName } = this.state;

    if (opportunity.customer) {
      return [
        {
          id: PaymentTypesEnum.preTax,
          name: productPaymentTypes.pre_tax,
          value: PaymentTypesEnum.preTax,
        },
        {
          id: PaymentTypesEnum.directDebit,
          name: productPaymentTypes.direct_debit,
          value: PaymentTypesEnum.directDebit,
        },
        {
          id: PaymentTypesEnum.nonDirectDebit,
          name: productPaymentTypes.non_direct_debit,
          value: PaymentTypesEnum.nonDirectDebit,
        },
        {
          id: PaymentTypesEnum.employer,
          name: productPaymentTypes.employer,
          value: PaymentTypesEnum.employer,
        },
      ];
    } else {
      const preTax = [
        {
          id: PaymentTypesEnum.preTax,
          name: productPaymentTypes.pre_tax,
          value: PaymentTypesEnum.preTax,
        },
      ];

      const nonPreTax = [
        {
          id: PaymentTypesEnum.postTax,
          name: productPaymentTypes.post_tax,
          value: PaymentTypesEnum.postTax,
        },
        {
          id: PaymentTypesEnum.preAndPostTax,
          name: productPaymentTypes.pre_and_post_tax,
          value: PaymentTypesEnum.preAndPostTax,
        },
      ];

      if (
        pretaxProductName === preTaxProducts.evergreen ||
        pretaxProductName === preTaxProducts.paps
      ) {
        return preTax;
      } else {
        return [...preTax, ...nonPreTax];
      }
    }
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  async componentDidMount(): Promise<void> {
    await this.triggerImmediateSearch();
    const contributionType = this.props.opportunity.contributionType as string;
    const product = this.props.opportunity.product;
    await Promise.all([
      this.props.setPercentage(90),
      this.getCountries(),
      this.showCategoryandTier(product?.id),
      this.showContributionFields(contributionType),
      this.handleProductPaymentTypeSelect(
        this.props.opportunity.productPaymentType as string,
      ),
    ]);
    this.getAllSalesPerson();
  }

  assignProductToOpportunity = ({
    product,
    monthlyExpectedContribution,
    lumpsumExpectedContribution,
    sourceOfAwareness,
    signupReason,
    productPaymentType,
    contributionType,
    contributionAllocationTypes,
    salesPersonId,
  }: AssignProductToOpportunityProps): Promise<AxiosResponse<void>> => {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_PRODUCT_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        productId: product,
        monthlyExpectedContribution,
        lumpsumExpectedContribution,
        sourceOfAwareness,
        signupReason,
        productPaymentType,
        contributionType,
        contributionAllocationTypes,
        salesPersonId,
      },
    });
  };

  assignEmployerToOpportunity = async (employerId?: string): Promise<void> => {
    interface Response {
      opportunity: Opportunity;
      errors: APIError[];
    }
    const { opportunity } = this.props;

    const fieldsToBeValidated = [
      "employerName",
      "contactPersonFirstName",
      "contactPersonLasttName",
      "contactPersonGender",
      "contactPersonCountryId",
      "contactPersonRelationship",
      "contactPersonPhoneNumber",
      "contactPersonEmailAddress",
    ];

    if (employerId === undefined && !this.employerReference.current) return;

    let data;

    try {
      data =
        this.employerReference.current &&
        (await this.employerReference.current.validateFields(
          fieldsToBeValidated,
        ));
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });
    let final = {};
    final = data && {
      employerName: data.employerName,
      contactPersonFirstName: data.contactPersonFirstName,
      contactPersonLasttName: data.contactPersonLasttName,
      contactPersonGender: data.contactPersonGender,
      contactPersonCountryId: data.contactPersonCountryId,
      contactPersonRelationship: data.contactPersonRelationship,
      contactPersonPhoneNumber: data.contactPersonPhoneNumber,
      contactPersonEmailAddress: data.contactPersonEmailAddress,
    };

    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_EMPLOYER_TO_OPPORTUNITY,
        variables: {
          opportunityId: opportunity.id,
          customerId: opportunity.customer?.id,
          employerId: employerId ? employerId : null,
          newCompany: this.state.newCompany,
          ...final,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignEmployerToOpportunity",
      );

      this.setState({ isLoading: false });

      if (err) {
        message.error(`An Error Occured - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Failed to continue - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isLoading: false });
    }
  };

  showContributionFields = (contributionType: string): void => {
    const preservedHiddenFields = this.state.fieldsToHide.filter(
      (field) =>
        field !== "monthlyExpectedContribution" &&
        field !== "lumpsumExpectedContribution",
    );
    const contributionState = getContributionAmountFieldState(contributionType);
    const fieldsToHide: string[] = [
      ...contributionState.hiddenFields,
      ...preservedHiddenFields,
    ];

    if (contributionType === ContributionTypesEnum.lumpSum) {
      this.setState({
        fieldsToHide: fieldsToHide,
        selectedContributionType: true,
        selectedMonthlyAndLumpSumContributionType: false,
      });
    } else if (contributionType === ContributionTypesEnum.monthlyContribution) {
      this.setState({
        fieldsToHide: fieldsToHide,
        selectedContributionType: true,
        selectedMonthlyAndLumpSumContributionType: false,
      });
    } else if (contributionType === ContributionTypesEnum.monthlyAndLump) {
      this.setState({
        fieldsToHide: fieldsToHide,
        selectedContributionType: true,
        selectedMonthlyAndLumpSumContributionType: true,
      });
    } else {
      this.setState({
        fieldsToHide: fieldsToHide,
        selectedContributionType: false,
        selectedMonthlyAndLumpSumContributionType: false,
      });
    }
  };

  showCategoryandTier = (productId: string | undefined): void => {
    const { petraProducts } = this.props;
    let product: Product | undefined;
    if (this.props.editingState) {
      product = this.props.opportunity.product;
    } else if (productId) {
      product = petraProducts.find((product: Product): boolean => {
        return product.id === productId;
      });
    }
    let tier = "";
    if (product) {
      if (product.productTier.name === "tier_2") {
        tier = "Tier 2";
      } else if (product.productTier.name === "tier_3") {
        tier = "Tier 3";
      } else {
        tier = "Tier 4";
      }
    }

    const categoryTier = product
      ? `${product?.productCategory.name} - ${tier}`
      : "";

    const getFieldValue = this.reference.current?.getFieldValue;
    const setFieldsValue = this.reference.current?.setFieldsValue;
    const selectedContributionAllocationTypes =
      typeof getFieldValue === "function"
        ? getFieldValue("contributionAllocationTypes") ?? []
        : this.props.opportunity.contributionAllocationTypes ?? [];

    const sanitizedContributionAllocationTypes =
      sanitizeContributionAllocationTypes(
        selectedContributionAllocationTypes,
        product?.name,
      );

    if (
      sanitizedContributionAllocationTypes.length !==
      selectedContributionAllocationTypes.length
    ) {
      typeof setFieldsValue === "function" &&
        setFieldsValue({
          contributionAllocationTypes: sanitizedContributionAllocationTypes,
        });
    }

    this.setState({
      productCategoryAndTier: categoryTier,
      pretaxProductName: product?.name,
    });
  };

  handleFieldsToHide = (removeFields: string[]): string[] => {
    return this.state.fieldsToHide.filter(
      (field) => !removeFields.includes(field),
    );
  };

  async next(): Promise<void> {
    interface Response {
      opportunity: Opportunity;
      errors: APIError[];
    }
    const fieldsToBeValidated = [
      "product",
      "monthlyExpectedContribution",
      "lumpsumExpectedContribution",
      "sourceOfAwareness",
      "signupReason",
      "productPaymentType",
      "contributionType",
      ...(!this.props.opportunity.customer
        ? ["contributionAllocationTypes"]
        : []),
      "employerId",
      "salesPersonId",
    ];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    if (!this.props.opportunity.customer) {
      data = {
        ...data,
        contributionAllocationTypes: sanitizeContributionAllocationTypes(
          (data as AssignProductToOpportunityProps).contributionAllocationTypes,
          this.state.pretaxProductName,
        ),
      };
    }

    data = normalizeContributionAmountPayload(data as AssignProductToOpportunityProps);

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<any> =
        await this.assignProductToOpportunity(
          data as AssignProductToOpportunityProps,
        );

      const { err, mutation } = responseHandler<Response>(
        response,
        "assignProductToOpportunity",
      );

      if (mutation?.opportunity) {
        if (this.props.editingState && this.props.save) {
          this.props.save();
        } else {
          if (data.employerId != null) {
            await this.assignEmployerToOpportunity(data.employerId);
          } else {
            this.setState({ isLoading: false });
          }
          this.props.next();
        }
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
        message.error(`An Error Occured - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Failed to continue - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isLoading: false });
    }
  }

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ q: searchTerm });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q } = this.state;
    interface Response {
      data: {
        companySearch: {
          count: number;
          results: Company[];
        };
      };
    }

    this.setState({ isLoading: true });

    try {
      request({
        query: SEARCH_COMPANY_SUMMARY,
        variables: { q, page: 1 },
      })
        .then(({ data }: AxiosResponse<Response>): void => {
          this.setState({ companies: data.data.companySearch.results });
        })
        .finally((): void => {
          this.setState({ isLoading: false });
        });
    } catch (error) {
      this.setState({
        isLoading: false,
      });

      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  formatCompaniesToEmployerSelectOptions = (
    companies: Company[],
  ): SelectOptionsInterface[] => {
    const modifiedCompanies = convertCompaniesToSelectOptions(companies);
    return [
      { id: "new_account", name: "Create New Employer" },
      ...modifiedCompanies,
    ];
  };

  handleCompanySelect = async (id: string): Promise<void> => {
    if (id === "new_account") {
      this.setState({
        showEmployerModal: true,
        newCompany: true,
      });
    } else {
      this.setState({
        fieldsToHide: this.state.fieldsToHide,
        newCompany: false,
      });
    }
  };

  handleProductPaymentTypeSelect = async (id: string): Promise<void> => {
    const product = this.state.pretaxProductName as string;
    const isSb = isSavingsBoosterProduct(product);
    if (isSb && id === PaymentTypesEnum.employer) {
      const results = this.handleFieldsToHide(["employerId"]);
      this.setState({
        fieldsToHide: results,
        selectingEmployer: true,
      });
    } else {
      this.setState({
        fieldsToHide: ["employerId", ...this.state.fieldsToHide],
        selectingEmployer: false,
      });
    }
  };

  NewEmployerUI = (fields: FormFieldConfig[]): JSX.Element => {
    return (
      <BaseForm
        fieldsToHide={[]}
        layout="vertical"
        reference={this.employerReference}
        fields={fields}
      ></BaseForm>
    );
  };

  toggleAddEmployerModal = (): void => {
    this.setState((previousState: Readonly<ProductFormState>) => ({
      showEmployerModal: !previousState.showEmployerModal,
    }));
  };

  addEmployer = (): void => {
    this.assignEmployerToOpportunity(undefined);
  };

  getCountries = (): void => {
    interface Response {
      data: {
        countries: Country[];
      };
    }
    request({
      query: GET_COUNTRIES,
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({ countries: data.data.countries });
    });
  };

  getAllSalesPerson(): void {
    const { salesPersonSearchTerm } = this.state;
    request({
      query: GET_SALES_PEOPLE,
      variables: {
        page: 1,
        q: salesPersonSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        salesPersons: data.salesPeople.results,
        isLoading: false,
      });
    });
  }

  triggerSalesPersonDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllSalesPerson();
  }, DEBOUNCE_TIME);

  handleSalesPersonSearchInput = async (
    salesPersonSearchTerm: string,
  ): Promise<void> => {
    this.setState({ salesPersonSearchTerm }, () => {
      this.triggerSalesPersonDelayedSearch();
    });
  };

  render(): JSX.Element {
    const { petraProducts, opportunity, editingState } = this.props;
    const {
      isLoading,
      companies,
      fieldsToHide,
      countries,
      salesPersons,
    } = this.state;

    const usersPlusSalesPerson = getUniqueArrayObjects(
      opportunity?.salesPerson
        ? [
            ...salesPersons,
            {
              id: opportunity.salesPerson.id,
              name: opportunity.salesPerson.name,
            },
          ]
        : salesPersons,
    );

    const isMonthlyOnlyContributionSelected =
      fieldsToHide.includes("lumpsumExpectedContribution") &&
      !fieldsToHide.includes("monthlyExpectedContribution");
    const visibleFieldsToHide = fieldsToHide.filter(
      (field) =>
        field !== "monthlyExpectedContribution" &&
        field !== "lumpsumExpectedContribution",
    );
    const monthlyContributionDisabled = fieldsToHide.includes(
      "monthlyExpectedContribution",
    );
    const lumpSumContributionDisabled = fieldsToHide.includes(
      "lumpsumExpectedContribution",
    );
    const contributionAllocationTypeOptions =
      getContributionAllocationTypeOptions(this.state.pretaxProductName);

    const fields: FormFieldConfig[] = [
      {
        id: "product",
        name: "product",
        inputType: "select",
        rule: "string",
        label: "Product",
        placeholder: "Select Product",
        required: true,
        colConfig: {
          span: 10,
        },
        initialValue: opportunity.product ? opportunity.product.id : undefined,
        selectOptions: remodelProducts(petraProducts),
        changeHandler: this.showCategoryandTier,
      },
      {
        id: "pCategory",
        name: "pCategory",
        inputType: "description",
        disabled: true,
        label: "Product Category and Tier",
        placeholder: "No Product Has Been Selected",
        required: false,
        colConfig: {
          offset: 1,
          span: 10,
        },
        value: this.state.productCategoryAndTier,
      },
      {
        id: "productPaymentType",
        name: "productPaymentType",
        inputType: "select",
        rule: "string",
        label: "Product Payment Type",
        placeholder: "Select Payment Type",
        required: false,
        colConfig: {
          span: 10,
        },
        initialValue: opportunity.productPaymentType,
        selectOptions: this.generateProductPaymentTypes(),
        selectHandler: this.handleProductPaymentTypeSelect,
        changeHandler: this.handleProductPaymentTypeSelect,
      },
      {
        id: "contributionType",
        name: "contributionType",
        inputType: "select",
        rule: "string",
        placeholder: "Contribution Type",
        label: "Contribution Type",
        initialValue: opportunity.contributionType,
        selectOptions: generateContributionTypes(),
        changeHandler: this.showContributionFields,
        required: false,
        colConfig: {
          offset: 1,
          span: 10,
        },
      },
      {
        id: "signupReason",
        name: "signupReason",
        inputType: "select",
        rule: "string",
        label: "Why did you choose this product?",
        initialValue: opportunity.signupReason,
        selectOptions: signUpReason,
        placeholder: "Enter a reason for choosing this product...",
        required: false,
        colConfig: {
          span: 10,
        },
      },
      {
        id: "sourceOfAwareness",
        name: "sourceOfAwareness",
        inputType: "select",
        rule: "string",
        label: "How did you hear about Petra?",
        initialValue: opportunity.sourceOfAwareness,
        placeholder: "Choose source of awareness",
        required: true,
        selectOptions: sourceOfAwareness,
        colConfig: {
          offset: 1,
          span: 10,
        },
      },
      ...(!opportunity.customer
        ? [
            {
              id: `contributionAllocationTypes-${
                this.state.pretaxProductName || "default"
              }`,
              name: "contributionAllocationTypes",
              inputType: "multipleSelect",
              rule: "array",
              label: "Contribution Allocation Types",
              placeholder: "Select contribution allocation types...",
              required: false,
              initialValue: opportunity.contributionAllocationTypes ?? [],
              selectOptions: contributionAllocationTypeOptions,
              colConfig: {
                span: 10,
              },
            } as FormFieldConfig,
          ]
        : []),
      {
        id: "salesPersonId",
        name: "salesPersonId",
        placeholder: "Enter user's name ...",
        required: false,
        inputType: "search",
        label: "Sales Person",
        changeHandler: this.handleSalesPersonSearchInput,
        initialValue: opportunity.salesPerson && opportunity.salesPerson.id,
        selectOptions: usersPlusSalesPerson,
        colConfig: {
          offset: 1,
          span: 10,
        },
      },
      {
        id: "employerId",
        name: "employerId",
        inputType: "search",
        rule: "string",
        label: "Select Employer",
        placeholder: "Select Employer",
        required: false,
        initialValue: opportunity.employerId,
        colConfig: {
          offset: 1,
          span: 10,
        },
        selectOptions: this.formatCompaniesToEmployerSelectOptions(companies),
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCompanySelect,
      },
      {
        id: "lumpsumExpectedContribution",
        name: "lumpsumExpectedContribution",
        inputType: "number",
        rule: "number",
        label: "Lump Sum Expected Contribution",
        placeholder: "Lump Sum Expected Contribution",
        required: false,
        disabled: lumpSumContributionDisabled,
        initialValue: opportunity.lumpsumExpectedContribution,
        elementStyle: { width: "100%" },
        colConfig: {
          offset: 0,
          span: 10,
        },
      },
      {
        id: "monthlyExpectedContribution",
        name: "monthlyExpectedContribution",
        inputType: "number",
        rule: "number",
        label: "Monthly Expected Contribution",
        placeholder: "Monthly Expected Contribution",
        required: false,
        disabled: monthlyContributionDisabled,
        initialValue: opportunity.monthlyExpectedContribution,
        elementStyle: { width: "100%" },
        colConfig: {
          span: 10,
        },
      },
    ];

    // The two-column layout is a single wrapping Ant Design Row. Because
    // fields are hidden conditionally (existing customer, contribution type,
    // employer selection), hardcoded per-field offsets drift and leave fields
    // like Sales Person or Monthly Expected Contribution indented at the start
    // of a row. Recompute offsets from the actual sequence of visible fields so
    // each one alternates left (offset 0) / right (offset 1) and the grid stays
    // aligned regardless of which fields are shown.
    let visibleColumnIndex = 0;
    const alignedFields: FormFieldConfig[] = fields.map((field) => {
      if (fieldsToHide.includes(field.name)) {
        return field;
      }
      const offset = visibleColumnIndex % 2 === 0 ? 0 : 1;
      visibleColumnIndex += 1;
      return {
        ...field,
        colConfig: { ...field.colConfig, offset },
      };
    });

    const employerFields: FormFieldConfig[] = [
      {
        id: "employerName",
        name: "employerName",
        inputType: "string",
        rule: "string",
        label: "Company Name",
        placeholder: "Enter Employers Name",
        required: true,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "contactPersonFirstName",
        name: "contactPersonFirstName",
        inputType: "string",
        rule: "string",
        label: "Contact Person's First Name",
        placeholder: "Contact Person's First Name",
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "contactPersonLastName",
        name: "contactPersonLastName",
        inputType: "string",
        rule: "string",
        label: "Contact Person's Last Name",
        placeholder: "Contact Person's Last Name",
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "contactPersonGender",
        name: "contactPersonGender",
        inputType: "select",
        rule: "string",
        label: "Contact Person's Gender",
        placeholder: "Contact Person's Gender",
        selectOptions: [
          { id: "0", name: "Male", value: "male" },
          { id: "1", name: "Female", value: "female" },
        ],
        required: true,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "contactPersonCountryId",
        name: "contactPersonCountryId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Contact Person's Nationality",
        label: "Contact Person's Nationality",
        selectOptions: countries,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "contactPersonRelationship",
        name: "contactPersonRelationship",
        inputType: "string",
        rule: "string",
        label: "Contact Person's Designation",
        placeholder: "Contact Person's Designation",
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "contactPersonPhoneNumber",
        name: "contactPersonPhoneNumber",
        inputType: "phone",
        rule: "string",
        label: "Contact Person's Phone Number",
        placeholder: "Contact Person's Phone Number",
        required: true,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "contactPersonEmailAddress",
        name: "contactPersonEmailAddress",
        inputType: "email",
        rule: "string",
        label: "Contact Person's Email Address",
        placeholder: "Contact Person's Email Address",
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          cancelEdit={this.props.cancelEdit}
          config={{
            prev: this.props.prev,
            next: this.next,
            save: this.next,
            showNext: !editingState,
            showPrev: !editingState,
            showSave: editingState,
            showCancelEdit: true,
          }}
        >
          <Fragment>
            <div>{this.props.opportunityPreview}</div>
            <div>
              <BaseForm
                fieldsToHide={visibleFieldsToHide}
                layout="vertical"
                reference={this.reference}
                fields={alignedFields}
              ></BaseForm>
            </div>
            {this.state.showEmployerModal &&
              ModalCreator({
                title: "Add New Employer",
                okType: "primary",
                okText: "Add Employer",
                width: 800,
                isLoading: this.state.isLoading,
                showModal: this.state.showEmployerModal,
                onOkay: this.addEmployer,
                toggleModal: this.toggleAddEmployerModal,
                showFooter: true,
                inscription: this.NewEmployerUI(employerFields),
              })}
          </Fragment>
        </StepContainer>
      </Spin>
    );
  }
}
